'use client';

import classNames from 'classnames';
import { useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import useUser from '@alltrails/context/hooks/useUser';
import { useFormatMessage } from '@alltrails/core/hooks/useFormatMessage';
import { useLanguageRegionCode } from '@alltrails/core/hooks/useLanguageRegionCode';
import { wrapUrlSafe } from '@alltrails/core/utils/wrapUrlSafe';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { SPACE_16 } from '@alltrails/denali/tokens';
import type { HeroData } from '../../utils/getHeroImages';
import HomeSearchBar from '../HomeSearchBar';
import RotatingHero from '../RotatingHero';
import styles from './styles.module.scss';

const messages = defineMessages({
  FIND_YOUR_OUTDOORS: {
    defaultMessage: 'Find your next adventure'
  }
});

type Props = {
  heroData: HeroData;
};

// Design: https://www.figma.com/design/0yD9YXMqR17WjqlJMRQwpd/2026-Homepage-update?node-id=612-7627&m=dev
const HomeHeader = ({ heroData }: Props) => {
  const languageRegionCode = useLanguageRegionCode();
  const intl = useIntl();
  const user = useUser();
  const {
    formattedDefaultMessages: { FIND_YOUR_OUTDOORS }
  } = useFormatMessage(messages);
  const isMobile = useIsMobileSizedScreen();
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);

  const headerText = user?.firstName
    ? intl.formatMessage({ defaultMessage: 'Let’s get outside, {firstName}' }, { firstName: user.firstName })
    : FIND_YOUR_OUTDOORS;

  const params = new URLSearchParams({ trigger: 'home' });
  if (user) {
    const buttonContainerStyle = isMobile ? { marginBottom: SPACE_16 } : undefined;

    return (
      <div>
        <RotatingHero
          images={heroData.images}
          className={classNames(styles.loggedInContainer, { [styles.authenticated]: user })}
          buttonContainerStyle={buttonContainerStyle}
        >
          <div className={styles.gradient} />
          <h1 className={styles.header}>{isMounted ? headerText : ''}</h1>
          <div className={classNames(styles.searchBarContainer, styles.loggedInSearchContainer)}>
            <HomeSearchBar />
          </div>
        </RotatingHero>
        <div className={styles.createRouteLinkContainer}>
          <a className={styles.createRouteLink} href={wrapUrlSafe(`/explore/custom-routes/new?${params.toString()}`, languageRegionCode)}>
            <FormattedMessage defaultMessage="Create a route" />
          </a>
        </div>
      </div>
    );
  }

  return (
    <RotatingHero images={heroData.images} className={styles.container}>
      <div className={styles.gradient} />
      <div className={styles.contentContainer}>
        <h1 className={classNames(styles.header, styles.loggedOutHeader)}>{headerText}</h1>
        <div className={styles.searchBarContainer}>
          <HomeSearchBar />
        </div>
        <div className={styles.exploreLink}>
          <a href={wrapUrlSafe('/explore', languageRegionCode)}>
            <FormattedMessage defaultMessage="Explore nearby trails" />
          </a>
          <a href={wrapUrlSafe(`/explore/custom-routes/new?${params.toString()}`, languageRegionCode)}>
            <FormattedMessage defaultMessage="Create a route" />
          </a>
        </div>
      </div>
    </RotatingHero>
  );
};

export default HomeHeader;
